import { Action, AnyAction, Middleware } from 'redux';

import { useNavigate } from 'react-router-dom';
import { IApi as IOldApi } from '../api';
import { AuthenticationError } from '../api/authentication';
import { ApiRequestError } from '../api/errors';
import { IApi } from '../data/api';
import { sessionActions } from '../entities/auth/session/actions';
import { logging } from '../logging';
import { IState } from './reducer';

export interface IThunkDispatch {
	<T extends Action = AnyAction>(action: T): T;
	<R>(asyncAction: ThunkAction<R>): R;
}

export type Dispatchable<T = any> = Action<T> | ThunkAction<Action<T> | ThunkAction<T>>;

interface IThunkExtraArgument {
	_deprecatedApi: IOldApi;
	api: IApi;
}

export type ThunkAction<R = any> = (dispatch: IThunkDispatch, getState: () => IState, extra: IThunkExtraArgument) => R;

export type ThunkMiddleware = Middleware<IThunkDispatch, IState, IThunkDispatch>;

export function thunks(api: IApi, oldApi: IOldApi): Middleware {
	return createThunkMiddleware({ _deprecatedApi: oldApi, api });
}

const isPromise = (obj: any): obj is Promise<any> => !!obj && typeof obj.then === 'function';

const promiseAction = (action: Promise<any>, dispatch: IThunkDispatch, context: any) => {
	return action.catch((error) => {
		const log = { error, context, payload: {} };
		if (error instanceof AuthenticationError) {
			dispatch(sessionActions.SESSION_INVALID({ error }));
			const navigate = useNavigate();
			navigate('/');
			logging.error({ message: error.message, error, context, payload: null });
			return Promise.reject(error);
		} else if (error instanceof ApiRequestError) {
			log.payload = error.data;
			logging.error({ message: error.message, error, context, payload: error.data });
			return Promise.resolve();
		} else {
			logging.error({ message: error.message, error, context, payload: null });
			dispatch({ type: 'UNCAUGHT_ACTION_ERROR', payload: error });
			return Promise.reject(error);
		}
	}).then(dispatch);
};

function createThunkMiddleware(extraArgument: IThunkExtraArgument): ThunkMiddleware {
	return ({ dispatch, getState }) => (next) => (action: AnyAction | ThunkAction) => {
		if (!action) return;

		const owner = getState().entities.auth.session.current.ownerId || '';

		if (isPromise(action)) return promiseAction(action, dispatch, { owner });

		if (typeof action === 'function') return dispatch(action(dispatch, getState, extraArgument));

		logging.debug({ payload: action, error: {}, context: { owner } });
		return next(action);
	};
}
